import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Loader2, Users, ShieldCheck, Activity, Euro,
  Crown, Gift, RefreshCw, Plus, ShoppingBag,
  Search, X, ArrowUp, ArrowDown, ArrowUpDown,
  TrendingUp, TrendingDown, UserMinus, Target, AlertTriangle,
  PieChart, BarChart3, Bell, PackageCheck,
  Sparkles, Brain, Lightbulb, CheckCircle2, AlertCircle,
  Coins, Calculator, Wallet, Gauge,
} from "lucide-react";

function formatEuro(v) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0,
  }).format(v || 0);
}

function formatEuro3(v) {
  // 3 decimali per valori piccoli (costo AI < 1€); 2 decimali sopra.
  const n = Number(v || 0);
  const digits = Math.abs(n) < 1 ? 3 : 2;
  return new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR",
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(n);
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT"); }
  catch { return iso; }
}

const PLAN_STYLE = {
  free:  "bg-[#F2EEE7] text-[#737373]",
  basic: "bg-[#EAE5DF] text-[#1A1A1A]",
  pro:   "bg-[#1A1A1A] text-[#C5A059]",
};

const STATUS_LABEL = {
  none: "—", active: "Attivo", cancelled: "Cancellato", expired: "Scaduto",
};

function computeSubStatus(u) {
  /**
   * Return {label, tone} based on subscription_end proximity.
   * - expired → "Scaduto" (red)
   * - <7 giorni → "In scadenza" (amber)
   * - cancelled but period still running → "Cancellato" (muted)
   * - active/future → "Attivo" (green)
   * - free plan → "—"
   */
  if (u.plan === "free") return { label: "—", tone: "muted" };
  const raw = u.subscription_status;
  if (raw === "expired") return { label: "Scaduto", tone: "danger" };
  if (!u.subscription_end) return { label: STATUS_LABEL[raw] || raw, tone: "muted" };
  const end = new Date(u.subscription_end);
  const now = new Date();
  const diffDays = (end - now) / 86400000;
  if (diffDays <= 0) return { label: "Scaduto", tone: "danger" };
  if (raw === "cancelled") return { label: "Cancellato", tone: "muted" };
  if (diffDays < 7) return { label: "In scadenza", tone: "warning" };
  return { label: "Attivo", tone: "success" };
}

const STATUS_CLASSES = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  danger:  "bg-red-50 text-red-700 border-red-200",
  muted:   "bg-[#F2EEE7] text-[#737373] border-[#E5E5E0]",
};

/**
 * HealthReportCard: card inline mostrata dopo aver lanciato l'analisi AI
 * del business. Badge colore (verde/giallo/rosso/gray), punteggio grande,
 * sintesi e 3 colonne (problemi / opportunità / azioni).
 */
const HEALTH_TONES = {
  ottimo:     { bg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  buono:      { bg: "bg-emerald-50/70", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-400" },
  attenzione: { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  critico:    { bg: "bg-rose-50", border: "border-rose-200", badge: "bg-rose-100 text-rose-700", dot: "bg-rose-500" },
};

function HealthReportCard({ data, onRefresh, refreshing, onClose }) {
  if (!data?.report) return null;
  const r = data.report;
  const tone = HEALTH_TONES[r.stato_generale] || HEALTH_TONES.attenzione;
  const genDate = data.generated_at
    ? new Date(data.generated_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })
    : null;
  return (
    <div
      className={`rounded-2xl border ${tone.border} ${tone.bg} p-6 mb-8 animate-fade-up`}
      data-testid="admin-health-report"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-[#E5E5E0] flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
              Analisi AI — Stato business
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${tone.badge}`}
                data-testid="admin-health-badge"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                {r.stato_generale?.toUpperCase()}
              </span>
              <span className="font-serif-display text-2xl text-[#1A1A1A]" data-testid="admin-health-score">
                {r.punteggio}<span className="text-base text-[#737373]">/100</span>
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.from_cache && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#F2EEE7] text-[#737373]">
              Cache
            </span>
          )}
          {genDate && (
            <span className="text-xs text-[#737373]">Generato {genDate}</span>
          )}
          <button
            onClick={() => onRefresh(true)}
            disabled={refreshing}
            className="h-9 px-3 rounded-lg border border-[#E5E5E0] bg-white hover:border-[#C5A059] text-xs font-medium inline-flex items-center gap-1.5 transition-all disabled:opacity-60"
            data-testid="admin-health-refresh"
            title="Rigenera analisi (forza, ignora cache)"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Rigenera
          </button>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-[#E5E5E0] bg-white hover:border-[#C5A059] flex items-center justify-center"
            aria-label="Chiudi"
            data-testid="admin-health-close"
          >
            <X className="w-4 h-4 text-[#737373]" />
          </button>
        </div>
      </div>

      {r.sintesi && (
        <p className="text-[15px] text-[#1A1A1A] leading-relaxed mb-6" data-testid="admin-health-summary">
          {r.sintesi}
        </p>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <HealthList
          title="Problemi principali"
          icon={AlertCircle}
          iconCls="text-rose-600"
          items={r.problemi_principali}
          testid="admin-health-problems"
        />
        <HealthList
          title="Opportunità"
          icon={Lightbulb}
          iconCls="text-amber-600"
          items={r.opportunita}
          testid="admin-health-opportunities"
        />
        <HealthList
          title="Azioni consigliate"
          icon={CheckCircle2}
          iconCls="text-emerald-600"
          items={r.azioni_consigliate}
          testid="admin-health-actions"
        />
      </div>
    </div>
  );
}

function HealthList({ title, icon: Icon, iconCls, items, testid }) {
  return (
    <div className="rounded-xl bg-white border border-[#E5E5E0] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${iconCls}`} />
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium">{title}</p>
      </div>
      {items && items.length > 0 ? (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="text-[13px] text-[#1A1A1A] leading-relaxed flex items-start gap-2">
              <span className="text-[#C5A059] mt-1">•</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-[#A3A3A3]">—</p>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#C5A059] mb-3">
        <Icon className="w-4 h-4" />
        <p className="text-xs uppercase tracking-[0.2em] font-medium">{label}</p>
      </div>
      <p className="font-serif-display text-4xl text-[#1A1A1A]">{value}</p>
    </div>
  );
}

/**
 * KpiCard: compact variant used for the new PRO rows. Supports an optional
 * hint (secondary small text) and an optional "tone" for up/down/neutral
 * colour (used by MRR growth).
 */
function KpiCard({ icon: Icon, label, value, hint, tone = "neutral", testid }) {
  const valueToneCls =
    tone === "up" ? "text-emerald-700" :
    tone === "down" ? "text-rose-700" :
    "text-[#1A1A1A]";
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#C5A059] mb-2">
        <Icon className="w-4 h-4" />
        <p className="text-[11px] uppercase tracking-[0.18em] font-medium">{label}</p>
      </div>
      <p className={`font-serif-display text-3xl ${valueToneCls}`}>{value}</p>
      {hint && <p className="text-xs text-[#737373] mt-1.5">{hint}</p>}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  // AI business health
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  // CANTIERE ADMIN SETTINGS (giu 2026) — feature toggle globali.
  const [settings, setSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSavedFlash, setSettingsSavedFlash] = useState(false);

  // Filters
  const [fPlan, setFPlan] = useState("all");       // all | free | basic | pro
  const [fCycle, setFCycle] = useState("all");     // all | monthly | yearly
  const [fStatus, setFStatus] = useState("all");   // all | active | warning | cancelled | expired
  const [fUsoMin, setFUsoMin] = useState("");
  const [fUsoMax, setFUsoMax] = useState("");
  const [fSearch, setFSearch] = useState("");

  // Sorting
  const [sortField, setSortField] = useState(null); // 'email' | 'plan' | 'cycle' | 'uso' | 'status' | 'created_at' | 'subscription_end'
  const [sortDir, setSortDir] = useState("asc");    // 'asc' | 'desc'

  const resetFilters = () => {
    setFPlan("all"); setFCycle("all"); setFStatus("all");
    setFUsoMin(""); setFUsoMax(""); setFSearch("");
    setSortField(null); setSortDir("asc");
  };

  const toggleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortField(null);
      setSortDir("asc");
    }
  };

  const hasActiveFilters =
    fPlan !== "all" || fCycle !== "all" || fStatus !== "all" ||
    fUsoMin !== "" || fUsoMax !== "" || fSearch.trim() !== "" ||
    sortField !== null;

  const runHealthCheck = useCallback(async (force = false) => {
    setHealthLoading(true);
    try {
      const res = await api.post(`/admin/health-check${force ? "?force=true" : ""}`);
      setHealth(res.data);
      setHealthOpen(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail :
        detail?.message || "Errore nell'analisi AI del business"
      );
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [s, u, cfg] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/settings"),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setSettings(cfg.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento admin");
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleObservationsCache = useCallback(async (next) => {
    setSettingsSaving(true);
    try {
      const res = await api.patch("/admin/settings", {
        observations_cache_enabled: next,
      });
      setSettings(res.data);
      setSettingsSavedFlash(true);
      toast.success(
        next
          ? "Cache osservazioni AI attivata"
          : "Cache osservazioni AI disattivata"
      );
      setTimeout(() => setSettingsSavedFlash(false), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile aggiornare l'impostazione");
    } finally {
      setSettingsSaving(false);
    }
  }, []);

  useEffect(() => { if (user?.is_admin) refresh(); }, [user, refresh]);

  // Route guard
  if (authLoading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const runAction = async (userId, action, payload) => {
    const key = `${userId}-${action}`;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      if (action === "set-plan") {
        await api.post("/admin/set-plan", { user_id: userId, plan: payload });
        toast.success(`Piano aggiornato a ${payload.toUpperCase()}`);
      } else if (action === "add-analyses") {
        await api.post("/admin/add-analyses", { user_id: userId, count: payload });
        toast.success(`Aggiunte ${payload} analisi`);
      }
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore azione");
    } finally {
      setBusy(b => { const n = { ...b }; delete n[key]; return n; });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
        </div>
      </Layout>
    );
  }

  // Derived, filtered + sorted list
  const filteredUsers = (() => {
    const min = fUsoMin === "" ? null : Number(fUsoMin);
    const max = fUsoMax === "" ? null : Number(fUsoMax);
    const q = fSearch.trim().toLowerCase();

    const decorated = users.map((u) => {
      const uso = u.plan === "free" ? u.analyses_used_total : u.analyses_used_current_period;
      const cycleKey = u.plan === "free" ? "" : (u.billing_cycle === "yearly" ? "yearly" : "monthly");
      const sub = computeSubStatus(u);
      // Map the visual tone to a filter key the admin can pick.
      const statusKey =
        u.plan === "free" ? "none" :
        sub.tone === "success" ? "active" :
        sub.tone === "warning" ? "warning" :
        sub.tone === "danger"  ? "expired" :
        sub.label === "Cancellato" ? "cancelled" : "none";
      return { ...u, _uso: Number(uso) || 0, _cycleKey: cycleKey, _statusKey: statusKey, _statusLabel: sub.label };
    });

    const filtered = decorated.filter((u) => {
      if (fPlan !== "all" && u.plan !== fPlan) return false;
      if (fCycle !== "all" && u._cycleKey !== fCycle) return false;
      if (fStatus !== "all" && u._statusKey !== fStatus) return false;
      if (min !== null && u._uso < min) return false;
      if (max !== null && u._uso > max) return false;
      if (q) {
        const hay = `${u.email || ""} ${u.salon_name || ""} ${u.name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (sortField) {
      const dir = sortDir === "asc" ? 1 : -1;
      const planOrder = { free: 0, basic: 1, pro: 2 };
      filtered.sort((a, b) => {
        let va, vb;
        switch (sortField) {
          case "email":             va = (a.email || "").toLowerCase();  vb = (b.email || "").toLowerCase(); break;
          case "plan":              va = planOrder[a.plan] ?? -1;        vb = planOrder[b.plan] ?? -1; break;
          case "cycle":             va = a._cycleKey;                    vb = b._cycleKey; break;
          case "uso":               va = a._uso;                         vb = b._uso; break;
          case "status":            va = a._statusLabel;                 vb = b._statusLabel; break;
          case "created_at":        va = a.created_at || "";             vb = b.created_at || ""; break;
          case "subscription_end":  va = a.subscription_end || "";       vb = b.subscription_end || ""; break;
          default:                  va = 0;                              vb = 0;
        }
        if (va < vb) return -1 * dir;
        if (va > vb) return  1 * dir;
        return 0;
      });
    }
    return filtered;
  })();

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const selectClass =
    "h-10 px-3 pr-8 rounded-xl border border-[#E5E5E0] bg-white text-sm text-[#1A1A1A] focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all appearance-none cursor-pointer bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23737373%22 stroke-width=%222%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-no-repeat bg-[right_0.75rem_center]";
  const inputClass =
    "h-10 px-3 rounded-xl border border-[#E5E5E0] bg-white text-sm text-[#1A1A1A] focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all";

  return (
    <Layout>
      <div className="mb-10 animate-fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Area riservata</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] mb-2">Admin Dashboard</h1>
          <p className="text-[#737373]">Gestione utenti, piani e monitoraggio MRR.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={refresh}
            className="h-11 px-4 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] font-medium inline-flex items-center gap-2 transition-all"
            data-testid="admin-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Ricarica
          </button>
          <button
            onClick={() => runHealthCheck(false)}
            disabled={healthLoading}
            className="h-11 px-4 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium inline-flex items-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="admin-health-check-btn"
          >
            {healthLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Analisi AI...</>
            ) : (
              <><Brain className="w-4 h-4" /> Analizza stato business</>
            )}
          </button>
        </div>
      </div>

      {/* Overview */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-6" data-testid="admin-overview">
        <StatCard icon={Users}       label="Utenti totali"    value={stats?.total_users ?? 0}   testid="stat-total-users" />
        <StatCard icon={ShieldCheck} label="Utenti paganti"   value={stats?.paid_users ?? 0}    testid="stat-paid-users" />
        <StatCard icon={Activity}    label="Analisi totali"   value={stats?.total_analyses ?? 0} testid="stat-total-analyses" />
        <StatCard icon={ShoppingBag} label="Prodotti venduti" value={stats?.total_products_sold ?? 0} testid="stat-products-sold" />
        <StatCard icon={Euro}        label="MRR stimato"      value={formatEuro(stats?.mrr_eur)} testid="stat-mrr" />
      </div>

      {/* Impostazioni Sistema (feature toggle globali) */}
      <div
        className="rounded-2xl border border-[#E5E5E0] bg-white p-6 mb-6"
        data-testid="admin-system-settings"
      >
        <div className="flex items-center gap-2 text-[#C5A059] mb-4">
          <Gauge className="w-4 h-4" />
          <p className="text-[11px] uppercase tracking-[0.18em] font-medium">Impostazioni Sistema</p>
        </div>

        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="font-medium text-[#1A1A1A]">Cache osservazioni AI</h3>
              {settings && (
                <span
                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                    settings.observations_cache_enabled
                      ? "bg-[#EAF3EA] text-[#2F6D3B]"
                      : "bg-[#F3EAEA] text-[#8A2A2A]"
                  }`}
                  data-testid="settings-cache-status"
                >
                  {settings.observations_cache_enabled ? "Attivo" : "Disattivo"}
                </span>
              )}
              {settingsSavedFlash && (
                <span
                  className="text-[10px] uppercase tracking-wider text-[#2F6D3B] inline-flex items-center gap-1"
                  data-testid="settings-saved-flash"
                >
                  <CheckCircle2 className="w-3 h-3" /> Salvato
                </span>
              )}
            </div>
            <p className="text-sm text-[#737373] leading-relaxed">
              Se attiva, le stesse foto producono sempre la stessa osservazione senza
              richiamare l&apos;AI. Disattivala per forzare una nuova analisi su tutte le foto.
            </p>
          </div>

          {/* Toggle switch */}
          <button
            type="button"
            role="switch"
            aria-checked={!!settings?.observations_cache_enabled}
            disabled={!settings || settingsSaving}
            onClick={() =>
              toggleObservationsCache(!settings?.observations_cache_enabled)
            }
            className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${
              settings?.observations_cache_enabled ? "bg-[#1A1A1A]" : "bg-[#D4D0CA]"
            }`}
            data-testid="settings-cache-toggle"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                settings?.observations_cache_enabled ? "translate-x-8" : "translate-x-1"
              }`}
            />
            {settingsSaving && (
              <Loader2 className="w-3 h-3 animate-spin absolute -right-6 top-1/2 -translate-y-1/2 text-[#737373]" />
            )}
          </button>
        </div>
      </div>

      {/* PRO metrics — row 2 */}
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
          Metriche Pro
        </span>
        <span className="h-px flex-1 bg-[#E5E5E0]" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="admin-pro-metrics">
        <KpiCard
          icon={Target}
          label="Conversione"
          value={`${stats?.conversion_rate ?? 0}%`}
          hint={`${stats?.paid_users ?? 0} / ${stats?.total_users ?? 0}`}
          testid="kpi-conversion-rate"
        />
        <KpiCard
          icon={UserMinus}
          label="Churn rate"
          value={`${stats?.churn_rate ?? 0}%`}
          hint="Cancellazioni / paganti"
          testid="kpi-churn-rate"
        />
        <KpiCard
          icon={Euro}
          label="ARPU"
          value={formatEuro(stats?.arpu_eur)}
          hint="Ricavo medio per utente pagante"
          testid="kpi-arpu"
        />
        <KpiCard
          icon={BarChart3}
          label="Analisi / utente"
          value={stats?.avg_analyses_per_user ?? 0}
          hint="Media su tutti gli utenti"
          testid="kpi-avg-analyses"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Utenti inattivi"
          value={stats?.inactive_users ?? 0}
          hint="Nessuna analisi negli ultimi 7 giorni"
          testid="kpi-inactive-users"
        />
      </div>

      {/* Business Intelligence — row 3 */}
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
          Business intelligence
        </span>
        <span className="h-px flex-1 bg-[#E5E5E0]" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-bi-metrics">
        <KpiCard
          icon={(stats?.mrr_growth_eur ?? 0) >= 0 ? TrendingUp : TrendingDown}
          label="Crescita MRR (30gg)"
          value={`${(stats?.mrr_growth_eur ?? 0) >= 0 ? "+" : ""}${formatEuro(stats?.mrr_growth_eur)}`}
          hint="Nuovi paganti ultimi 30 giorni"
          tone={(stats?.mrr_growth_eur ?? 0) > 0 ? "up" : (stats?.mrr_growth_eur ?? 0) < 0 ? "down" : "neutral"}
          testid="kpi-mrr-growth"
        />
        <KpiCard
          icon={PackageCheck}
          label="Follow-up completati"
          value={stats?.followups_done ?? 0}
          hint="Totale follow-up generati"
          testid="kpi-followups-done"
        />
        <KpiCard
          icon={Bell}
          label="Da contattare"
          value={stats?.followups_pending ?? 0}
          hint="Clienti con ultima analisi > 30gg"
          testid="kpi-followups-pending"
        />
        <KpiCard
          icon={PieChart}
          label="Analisi per piano"
          value={`${stats?.avg_basic_usage ?? 0} / ${stats?.avg_pro_usage ?? 0}`}
          hint="Media Basic / Pro"
          testid="kpi-analyses-by-plan"
        />
      </div>

      {/* Cost & Margin — row 4 (AI real cost) */}
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
          Costo & margine AI
        </span>
        <span className="h-px flex-1 bg-[#E5E5E0]" />
        {stats?.ai_cost_model && (
          <span
            className="text-[10px] text-[#A3A3A3]"
            title={`Modello ${stats.ai_cost_model.model} · ${stats.ai_cost_model.cost_per_1k_input_eur}€/1K input · ${stats.ai_cost_model.cost_per_1k_output_eur}€/1K output · ~${stats.ai_cost_model.avg_input_tokens} input + ${stats.ai_cost_model.avg_output_tokens} output tok/analisi`}
          >
            modello: {stats.ai_cost_model.model.replace(/-\d{8}$/, "")}
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="admin-cost-metrics">
        <KpiCard
          icon={Calculator}
          label="Costo medio / analisi"
          value={formatEuro3(stats?.ai_avg_cost_per_analysis_eur)}
          hint={`Standard ${formatEuro3(stats?.ai_cost_model?.standard_cost_eur)} · Follow-up ${formatEuro3(stats?.ai_cost_model?.followup_cost_eur)}`}
          testid="kpi-ai-avg-cost"
        />
        <KpiCard
          icon={Coins}
          label="Costo totale AI"
          value={formatEuro3(stats?.ai_total_cost_eur)}
          hint={`Su ${stats?.total_analyses ?? 0} analisi`}
          testid="kpi-ai-total-cost"
        />
        <KpiCard
          icon={Gauge}
          label="Costo / utente"
          value={formatEuro3(stats?.ai_cost_per_user_eur)}
          hint="Costo AI medio per utente registrato"
          testid="kpi-ai-cost-per-user"
        />
        <KpiCard
          icon={Coins}
          label="Costo AI mensile"
          value={formatEuro3(stats?.ai_monthly_cost_eur)}
          hint="Stima ultimi 30 giorni"
          testid="kpi-ai-monthly-cost"
        />
        <KpiCard
          icon={Wallet}
          label="Profitto stimato"
          value={formatEuro(stats?.estimated_profit_eur)}
          hint="MRR − costo AI mensile"
          tone={(stats?.estimated_profit_eur ?? 0) > 0 ? "up" : "down"}
          testid="kpi-estimated-profit"
        />
      </div>

      {/* AI business health report — appare solo dopo click del pulsante */}
      {healthOpen && health && (
        <HealthReportCard
          data={health}
          onRefresh={runHealthCheck}
          refreshing={healthLoading}
          onClose={() => setHealthOpen(false)}
        />
      )}

      {/* Users table */}
      <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden" data-testid="admin-users-table">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium">Utenti registrati</p>
          <span className="text-xs text-[#737373]" data-testid="admin-users-count">
            {filteredUsers.length} / {users.length} utenti
          </span>
        </div>

        {/* Filter bar */}
        <div
          className="px-6 py-4 border-b border-[#E5E5E0] bg-[#FAFAFA] flex flex-wrap items-center gap-3"
          data-testid="admin-filters"
        >
          {/* Search */}
          <div className="relative flex-1 min-w-[220px] max-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <input
              type="text"
              value={fSearch}
              onChange={(e) => setFSearch(e.target.value)}
              placeholder="Email o salone..."
              className={`${inputClass} w-full pl-9 pr-9`}
              data-testid="admin-filter-search"
            />
            {fSearch && (
              <button
                onClick={() => setFSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md hover:bg-[#EAE5DF] flex items-center justify-center"
                aria-label="Svuota ricerca"
              >
                <X className="w-3.5 h-3.5 text-[#737373]" />
              </button>
            )}
          </div>

          {/* Plan */}
          <select
            value={fPlan}
            onChange={(e) => setFPlan(e.target.value)}
            className={selectClass}
            data-testid="admin-filter-plan"
            aria-label="Filtra per piano"
          >
            <option value="all">Piano: tutti</option>
            <option value="free">Free</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
          </select>

          {/* Cycle */}
          <select
            value={fCycle}
            onChange={(e) => setFCycle(e.target.value)}
            className={selectClass}
            data-testid="admin-filter-cycle"
            aria-label="Filtra per ciclo"
          >
            <option value="all">Ciclo: tutti</option>
            <option value="monthly">Mensile</option>
            <option value="yearly">Annuale</option>
          </select>

          {/* Status */}
          <select
            value={fStatus}
            onChange={(e) => setFStatus(e.target.value)}
            className={selectClass}
            data-testid="admin-filter-status"
            aria-label="Filtra per stato"
          >
            <option value="all">Stato: tutti</option>
            <option value="active">Attivo</option>
            <option value="warning">In scadenza</option>
            <option value="cancelled">Cancellato</option>
            <option value="expired">Scaduto</option>
          </select>

          {/* Uso min/max */}
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-[#737373]">Uso</span>
            <input
              type="number"
              min="0"
              value={fUsoMin}
              onChange={(e) => setFUsoMin(e.target.value)}
              placeholder="min"
              className={`${inputClass} w-[80px]`}
              data-testid="admin-filter-uso-min"
            />
            <span className="text-[#737373]">–</span>
            <input
              type="number"
              min="0"
              value={fUsoMax}
              onChange={(e) => setFUsoMax(e.target.value)}
              placeholder="max"
              className={`${inputClass} w-[80px]`}
              data-testid="admin-filter-uso-max"
            />
          </div>

          {/* Reset */}
          <button
            onClick={resetFilters}
            disabled={!hasActiveFilters}
            className="ml-auto h-10 px-4 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] text-sm font-medium inline-flex items-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-filter-reset"
          >
            <X className="w-3.5 h-3.5" /> Reimposta filtri
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#737373]">
              <tr>
                <th className="text-left px-6 py-3 font-medium">
                  <button onClick={() => toggleSort("email")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-email">
                    Email / Salone <SortIcon field="email" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium">
                  <button onClick={() => toggleSort("plan")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-plan">
                    Piano <SortIcon field="plan" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium">
                  <button onClick={() => toggleSort("cycle")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-cycle">
                    Ciclo <SortIcon field="cycle" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium">
                  <button onClick={() => toggleSort("uso")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-uso">
                    Uso <SortIcon field="uso" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium">
                  <button onClick={() => toggleSort("status")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-status">
                    Stato <SortIcon field="status" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium">
                  <button onClick={() => toggleSort("created_at")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-created">
                    Iscrizione <SortIcon field="created_at" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium">
                  <button onClick={() => toggleSort("subscription_end")} className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors" data-testid="sort-expiry">
                    Scadenza <SortIcon field="subscription_end" />
                  </button>
                </th>
                <th className="text-right px-6 py-3 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E0]">
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-14 text-center text-[#737373] text-sm" data-testid="admin-empty-state">
                    Nessun utente corrisponde ai filtri selezionati.
                  </td>
                </tr>
              )}
              {filteredUsers.map((u) => {
                const use = u.plan === "free" ? u.analyses_used_total : u.analyses_used_current_period;
                const key = u.id;
                const sub = computeSubStatus(u);
                const cycleLabel = u.plan === "free"
                  ? "—"
                  : u.billing_cycle === "yearly" ? "Annuale" : "Mensile";
                return (
                  <tr key={u.id} className="hover:bg-[#FAFAFA]" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-6 py-4">
                      <p className="text-[#1A1A1A] font-medium">{u.email}</p>
                      <p className="text-xs text-[#737373]">
                        {u.salon_name || u.name || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${PLAN_STYLE[u.plan] || PLAN_STYLE.free}`}>
                        {u.plan}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-[#1A1A1A]" data-testid={`admin-cycle-${u.id}`}>
                      {cycleLabel}
                    </td>
                    <td className="px-4 py-4 text-[#1A1A1A]">{use}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold border ${STATUS_CLASSES[sub.tone]}`}
                        data-testid={`admin-status-${u.id}`}
                      >
                        {sub.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-[#737373] text-xs">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-4 text-[#737373] text-xs" data-testid={`admin-expiry-${u.id}`}>
                      {u.plan === "free" ? "—" : formatDate(u.subscription_end)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2 justify-end">
                        <button
                          disabled={!!busy[`${key}-set-plan`]}
                          onClick={() => runAction(u.id, "set-plan", "pro")}
                          className="h-8 px-3 rounded-lg bg-[#1A1A1A] hover:bg-[#C5A059] text-white text-xs font-medium transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`btn-upgrade-pro-${u.id}`}
                        >
                          <Crown className="w-3 h-3" /> Pro
                        </button>
                        <button
                          disabled={!!busy[`${key}-set-plan`]}
                          onClick={() => runAction(u.id, "set-plan", "basic")}
                          className="h-8 px-3 rounded-lg border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] text-xs font-medium transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`btn-set-basic-${u.id}`}
                        >
                          <ShieldCheck className="w-3 h-3" /> Basic
                        </button>
                        <button
                          disabled={!!busy[`${key}-set-plan`]}
                          onClick={() => runAction(u.id, "set-plan", "free")}
                          className="h-8 px-3 rounded-lg border border-[#E5E5E0] hover:border-[#C5A059] text-[#737373] text-xs font-medium transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`btn-reset-free-${u.id}`}
                        >
                          <Gift className="w-3 h-3" /> Free
                        </button>
                        <button
                          disabled={!!busy[`${key}-add-analyses`]}
                          onClick={() => runAction(u.id, "add-analyses", 10)}
                          className="h-8 px-3 rounded-lg border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white text-xs font-medium transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`btn-add-10-${u.id}`}
                        >
                          <Plus className="w-3 h-3" /> +10
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
